/**
 * AgentMatchScan — V146.
 *
 * Public scan page. Two states:
 *   1. FORM     — email + URL inputs + scan button
 *   2. RESULTS  — top 5 ranked agents with personalized rationale,
 *                 bundle offer at top, per-agent CTAs. Auto-refreshes
 *                 from /scan/{id} every 6s while phase="heuristic" so
 *                 the Claude rerank shows up live without reload.
 *
 * Used as standalone /agent-match route + embeddable scan widget on
 * homepage hero / funnel.
 */
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, Loader2, ArrowRight, Clock, Euro, ShieldCheck, Mail, Globe, Zap } from "lucide-react";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import SiteInsightsPanel from "../components/agent-os/SiteInsightsPanel";
import PredictedROI from "../components/agent-os/PredictedROI";

const COPY = {
  sk: {
    h1_a: "Aký AI agent",
    h1_b: "ti najviac ušetrí čas?",
    sub: "Zadaj svoj web. Za 6 sekúnd ti pošleme TOP 5 agentov ušitých na mieru tvojmu biznisu.",
    email_label: "Tvoj email",
    email_placeholder: "tvoj@email.sk",
    url_label: "Tvoj web",
    url_placeholder: "https://tvojshop.sk",
    submit: "Spustiť bezplatný scan",
    submit_loading: "Skenujeme tvoj web…",
    err_email: "Zadaj platný email",
    err_url: "Zadaj platnú URL",
    err_generic: "Niečo zlyhalo, skús znova",
    results_eyebrow: "Tvoj personalizovaný report",
    results_h1: "Top 5 agentov pre tvoj web",
    results_for: "Pre",
    bundle_eyebrow: "Najlepší deal pre teba",
    bundle_h2: "Vezmi 3 agentov, plať len 2 (najlacnejší zadarmo)",
    bundle_save: "Ušetríš",
    bundle_per_month: "/mes",
    bundle_cta: "Vziať bundle",
    phase_heuristic: "⚡ Rýchle odporúčania pripravené · Claude AI deep analysis beží…",
    phase_full: "✅ Deep analysis hotová · Tvoj report poslaný emailom",
    saves_hours: "h/týždeň",
    saves_eur: "/mes úspora",
    coming_soon_badge: "🔬 Pripravujeme",
    cta_rent: "Vyskúšaj 14 dní zadarmo",
    cta_waitlist: "Pridaj sa na waitlist",
    sentence_above: "Tieto agenti budú robiť to, čo by si mal robiť ty.",
    why_us_eyebrow: "Prečo to funguje",
    why_us_p: "EU-native · GDPR · 14 dní zadarmo · zruš kedykoľvek",
    trust_p: "Tvoj scan ostáva uložený. Pošleme ti aj email s linkom.",
  },
  en: {
    h1_a: "Which AI agent",
    h1_b: "saves you the most time?",
    sub: "Drop in your website. In 6 seconds we'll email you TOP 5 agents tailored to YOUR business.",
    email_label: "Your email",
    email_placeholder: "you@email.com",
    url_label: "Your website",
    url_placeholder: "https://yourshop.com",
    submit: "Run free scan",
    submit_loading: "Scanning your site…",
    err_email: "Enter a valid email",
    err_url: "Enter a valid URL",
    err_generic: "Something failed, try again",
    results_eyebrow: "Your personalized report",
    results_h1: "Top 5 agents for your site",
    results_for: "For",
    bundle_eyebrow: "Your best deal",
    bundle_h2: "Take 3 agents, pay for 2 (cheapest is free)",
    bundle_save: "You save",
    bundle_per_month: "/mo",
    bundle_cta: "Grab the bundle",
    phase_heuristic: "⚡ Quick picks ready · Claude AI deep analysis running…",
    phase_full: "✅ Deep analysis complete · Report sent to your email",
    saves_hours: "h/week",
    saves_eur: "/mo savings",
    coming_soon_badge: "🔬 In pilot",
    cta_rent: "Try 14 days free",
    cta_waitlist: "Join the waitlist",
    sentence_above: "These agents will do what you should be doing.",
    why_us_eyebrow: "Why this works",
    why_us_p: "EU-native · GDPR · 14 days free · cancel anytime",
    trust_p: "Your scan is saved. We'll also email you the link.",
  },
};

function copyFor(lang) { return COPY[lang] || COPY.en; }

export default function AgentMatchScan() {
  const { language } = useLanguage();
  const c = copyFor(language);
  const params = useParams();
  const [email, setEmail] = useState("");
  const [url, setUrl] = useState("");
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Deep-link mode: /agent-match/scan/{id}
  useEffect(() => {
    if (!params.scanId) return;
    setLoading(true);
    axios.get(`${API}/agent-match/scan/${params.scanId}`)
      .then((r) => setScan(r.data))
      .catch(() => setErr("Scan not found"))
      .finally(() => setLoading(false));
  }, [params.scanId]);

  // Auto-refresh while phase=heuristic (Claude still running)
  useEffect(() => {
    if (!scan || scan.phase !== "heuristic") return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/agent-match/scan/${scan.id}`);
        if (r.data?.phase && r.data.phase !== "heuristic") {
          setScan(r.data);
          clearInterval(t);
        }
      } catch {}
    }, 6000);
    return () => clearInterval(t);
  }, [scan]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return setErr(c.err_email);
    if (url.length < 4) return setErr(c.err_url);
    setLoading(true); setErr("");
    try {
      const r = await axios.post(`${API}/agent-match/scan`, { email: email.trim().toLowerCase(), url: url.trim(), lang: language });
      setScan(r.data);
      window.history.replaceState({}, "", `/agent-match/scan/${r.data.id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || c.err_generic);
    } finally {
      setLoading(false);
    }
  };

  if (!scan) {
    return (
      <div className="min-h-screen bg-black text-white" data-testid="agent-match-page">
        <section className="px-4 sm:px-6 py-16 sm:py-24" style={{ background: "radial-gradient(ellipse at top, rgba(245,158,11,0.10) 0%, #0a0a0a 60%)" }}>
          <div className="max-w-3xl mx-auto">
            <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] font-black mb-4 text-amber-400 text-center">
              ⚡ ⚡ ⚡
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-center leading-[1.05] mb-5">
              <span className="text-white">{c.h1_a}</span>{" "}
              <span style={{ color: "#A3E635", textShadow: "0 0 24px rgba(163,230,53,0.4)" }}>{c.h1_b}</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-300 text-center mb-10 leading-relaxed">
              {c.sub}
            </p>

            <form onSubmit={submit} data-testid="agent-match-form" className="space-y-3 max-w-xl mx-auto">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-black text-gray-400 mb-1.5 block">
                  <Globe className="w-3 h-3 inline mr-1" /> {c.url_label}
                </label>
                <input
                  type="text" required value={url} onChange={(e) => setUrl(e.target.value)}
                  placeholder={c.url_placeholder} data-testid="scan-url-input"
                  className="w-full bg-black border border-gray-700 px-3 py-3 text-white placeholder-gray-600 focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest font-black text-gray-400 mb-1.5 block">
                  <Mail className="w-3 h-3 inline mr-1" /> {c.email_label}
                </label>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder={c.email_placeholder} data-testid="scan-email-input"
                  className="w-full bg-black border border-gray-700 px-3 py-3 text-white placeholder-gray-600 focus:border-amber-500 focus:outline-none"
                />
              </div>
              <button
                type="submit" disabled={loading} data-testid="scan-submit-btn" data-cta
                className="w-full bg-gradient-to-r from-amber-400 to-amber-500 text-black font-black px-6 py-4 text-sm uppercase tracking-widest hover:from-amber-300 hover:to-amber-400 disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_0_40px_rgba(245,158,11,0.4)]"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {c.submit_loading}</> : <><Zap className="w-4 h-4" /> {c.submit} <ArrowRight className="w-4 h-4" /></>}
              </button>
              {err ? <p className="text-sm text-red-400 text-center">{err}</p> : null}
              <p className="text-xs text-gray-500 text-center pt-2 flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                {c.trust_p}
              </p>
            </form>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="agent-match-results">
      <section className="px-4 sm:px-6 py-12 sm:py-16" style={{ background: "radial-gradient(ellipse at top, rgba(245,158,11,0.10) 0%, #0a0a0a 60%)" }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.3em] font-black mb-3 text-amber-400">
            {c.results_eyebrow}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-2">{c.results_h1}</h1>
          <p className="text-sm text-gray-400 mb-2">
            {c.results_for} <strong className="text-amber-300" data-testid="scan-site-url">{scan.url}</strong>
            {scan.site?.title ? <span className="text-gray-500"> · {scan.site.title.slice(0, 60)}</span> : null}
          </p>
          <p className={`text-xs mb-8 flex items-center gap-1.5 ${scan.phase === "full" ? "text-emerald-400" : "text-amber-400"}`} data-testid="scan-phase-indicator">
            {scan.phase === "heuristic" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            {scan.phase === "full" ? c.phase_full : c.phase_heuristic}
          </p>

          {/* V146.1 — Deep site insights ("wow, you know that about my site?") */}
          <SiteInsightsPanel site={scan.site} />

          {/* V146.2 — Predicted ROI widget */}
          <PredictedROI results={scan.results} />

          {/* Bundle offer */}
          {scan.bundle?.applicable ? (
            <div className="border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-6 sm:p-8 mb-8" data-testid="scan-bundle">
              <p className="text-[10px] uppercase tracking-widest font-black text-amber-400 mb-2">
                <Sparkles className="w-3 h-3 inline mr-1" /> {c.bundle_eyebrow}
              </p>
              <h2 className="text-xl sm:text-2xl font-black mb-4 leading-tight">{c.bundle_h2}</h2>
              <div className="flex flex-wrap items-end gap-4 sm:gap-6">
                <div>
                  <p className="text-[10px] uppercase text-gray-400 font-black">{c.bundle_save}</p>
                  <p className="text-3xl sm:text-4xl font-black text-emerald-400">€{scan.bundle.savings_eur_month}{c.bundle_per_month}</p>
                </div>
                <div className="text-sm text-gray-400">
                  <p>€{scan.bundle.total_price_normal} → <strong className="text-amber-300">€{scan.bundle.total_price_bundle}{c.bundle_per_month}</strong></p>
                  <p className="text-xs">3 agenti za cenu 2</p>
                </div>
                <Link to="/agent-os/onboard" data-testid="bundle-cta" data-cta className="ml-auto bg-amber-500 text-black font-black px-6 py-3 text-xs uppercase tracking-widest hover:bg-amber-400 inline-flex items-center gap-2">
                  {c.bundle_cta} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ) : null}

          <p className="text-base text-gray-300 italic mb-6">{c.sentence_above}</p>

          {/* Top 5 agents */}
          <div className="space-y-3" data-testid="scan-agents-list">
            {scan.results.map((r, i) => (
              <div key={r.slug} data-testid={`scan-agent-${r.slug}`} className="border border-gray-800 bg-neutral-950 p-5 sm:p-6 hover:border-amber-500/60 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="text-3xl sm:text-4xl flex-shrink-0">{r.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest font-black text-amber-400">#{i + 1}</span>
                      <h3 className="text-lg sm:text-xl font-black text-white">{r.name}</h3>
                      {r.coming_soon ? (
                        <span className="text-[9px] uppercase tracking-widest font-black bg-amber-500/20 text-amber-300 px-2 py-0.5 border border-amber-500/30">
                          {c.coming_soon_badge}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-sm text-gray-300 leading-relaxed mb-3" data-testid={`scan-agent-${r.slug}-rationale`}>
                      {r.rationale}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <span className="inline-flex items-center gap-1 text-emerald-400 font-bold">
                        <Clock className="w-3.5 h-3.5" /> {r.hours_saved_week}{c.saves_hours}
                      </span>
                      <span className="inline-flex items-center gap-1 text-emerald-400 font-bold">
                        <Euro className="w-3.5 h-3.5" /> €{r.eur_saved_month.toLocaleString("sk-SK")}{c.saves_eur}
                      </span>
                      <span className="text-gray-500">·</span>
                      <span className="text-gray-400">€{r.price_eur_month}/mes</span>
                    </div>
                  </div>
                  <Link
                    to={r.coming_soon ? `/agent/${r.slug}` : "/agent-os/onboard"}
                    data-testid={`scan-agent-${r.slug}-cta`}
                    className="hidden sm:inline-flex items-center gap-1.5 self-center bg-amber-500 text-black font-black px-4 py-2.5 text-[11px] uppercase tracking-widest hover:bg-amber-400"
                  >
                    {r.coming_soon ? c.cta_waitlist : c.cta_rent}
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <Link
                  to={r.coming_soon ? `/agent/${r.slug}` : "/agent-os/onboard"}
                  className="sm:hidden mt-4 w-full inline-flex items-center justify-center gap-1.5 bg-amber-500 text-black font-black px-4 py-2.5 text-[11px] uppercase tracking-widest hover:bg-amber-400"
                >
                  {r.coming_soon ? c.cta_waitlist : c.cta_rent}
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>

          {/* Trust footer */}
          <div className="mt-10 text-center border-t border-gray-800 pt-6">
            <p className="text-[10px] uppercase tracking-widest font-black text-gray-500 mb-1">{c.why_us_eyebrow}</p>
            <p className="text-sm text-gray-300">{c.why_us_p}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
